<template>
    <div class="flex w-full p-6 flex-col items-start gap-4 rounded-lg bg-white dark:bg-slate-800 shadow">
        <header class="flex h-7 justify-between items-center self-stretch">
            <h2 class="text-gray-800 dark:text-gray-100 text-lg font-medium leading-7">Score history</h2>
            <button
                class="flex p-2 px-4 justify-center items-center gap-2 rounded bg-orange shadow-sm"
                @click.prevent="setActiveScore(null); openCreditScoreModal();"
            >
                <Icon icon="plus" svgClass="w-5 h-5" pathClass="stroke-white" />
                <span class="text-white text-sm font-medium leading-5">Add score</span>
            </button>
        </header>

        <!-- Column Headers - Always visible -->
        <div class="w-full">
            <div class="flex w-full border-b border-gray-200 dark:border-slate-700 py-4">
                <div class="text-gray-500 dark:text-gray-400 text-sm font-medium leading-5 w-24 sm:w-40">Date</div>
                <div class="w-6 sm:w-40"></div>
                <div class="text-gray-500 dark:text-gray-400 text-sm font-medium leading-5 grow">Score</div>
                <div class="w-20 sm:w-28"></div>
            </div>

            <!-- Loading State in data area -->
            <div v-if="loadingUserCreditScore" class="relative min-h-48 w-full">
                <LoadingState :loading="loadingUserCreditScore" />
            </div>

            <!-- Empty State Content -->
            <div v-if="!loadingUserCreditScore && emptyState" class="w-full">
                <creditscore-start :hideHeader="true"></creditscore-start>
            </div>

            <!-- Regular Content -->
            <div v-else-if="!loadingUserCreditScore && !emptyState">
                <div v-for="(score, index) in paginatedScores" :key="score.id" class="flex w-full py-2 items-center" :class="{'border-b border-gray-200 dark:border-slate-700': index !== paginatedScores.length - 1}">
                    <div class="w-24 sm:w-40 flex items-center text-slate-700 dark:text-slate-200 text-sm font-medium leading-5">{{ formatDate(score.score_date) }}</div>

                    <div class="w-6 sm:w-40"></div>

                    <div class="flex items-center grow pr-2 sm:pr-4">
                        <span class="text-gray-800 dark:text-gray-100 text-sm font-medium leading-5 w-8 sm:w-10 text-right">{{ score.score }}</span>

                        <div class="flex items-center gap-2 ml-2 sm:ml-4">
                            <div v-if="score.score >= 720" class="w-2 h-2 rounded-full bg-green-600 shrink-0"></div>
                            <div v-else-if="score.score >= 580 && score.score < 720" class="w-2 h-2 rounded-full bg-amber-500 shrink-0"></div>
                            <div v-else class="w-2 h-2 rounded-full bg-red-500 shrink-0"></div>

                            <span class="text-gray-500 dark:text-gray-400 text-sm font-normal leading-5">
                                {{ score.score >= 720 ? 'Excellent' : score.score >= 580 ? 'Good' : 'Poor' }}
                            </span>
                        </div>
                    </div>

                    <div class="flex items-center gap-1 sm:gap-2 w-20 sm:w-28 justify-end">
                        <button
                            class="flex p-1.5 sm:p-2 items-center justify-center rounded-md hover:bg-gray-100 dark:hover:bg-slate-700 dark:bg-slate-800"
                            @click.prevent="
                                setActiveScore(score);
                                openCreditScoreModal();
                            "
                            :id="`hs-credit-score-edit-${score.id}`"
                        >
                            <Icon icon="edit" svgClass="w-4 h-4 sm:w-5 sm:h-5" pathClass="fill-gray-500" />
                        </button>
                        <button
                            class="flex p-1.5 sm:p-2 items-center justify-center rounded-md hover:bg-gray-100 dark:hover:bg-slate-700 dark:bg-slate-800"
                            @click="handleDeleteClick(score)"
                            :id="`hs-credit-score-delete-${score.id}`"
                        >
                            <Icon icon="trash" svgClass="w-4 h-4 sm:w-5 sm:h-5" pathClass="fill-gray-500" />
                        </button>
                    </div>
                </div>
            </div>

            <ConfirmationDialog
                v-if="showDeleteScoreModal"
                :show="showDeleteScoreModal"
                title="Delete credit score entry"
                dialog="Are you sure you want to delete this credit score? This action cannot be undone."
                :buttons="[
                    {
                        text: 'Cancel',
                        value: false,
                        type: 'cancel',
                        classes: 'border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-100',
                    },
                    {
                        text: 'Delete',
                        value: true,
                        type: 'confirm',
                        classes: '!bg-red-500 text-white',
                    },
                ]"
                @cancel="closeDeleteScoreModal"
                @confirm="confirmDeleteScore"
            >
                <template #title>
                    <div class="flex items-center gap-3 self-stretch">
                        <div
                            class="flex items-center justify-center w-12 h-12 p-3 rounded-full bg-red-100"
                        >
                            <Icon icon="trash" pathClass="fill-red-500" />
                        </div>
                        <span
                            class="text-gray-800 dark:text-gray-100 font-inter text-xl font-medium leading-7"
                        >
                            Delete Credit Score Entry
                        </span>
                    </div>
                </template>
            </ConfirmationDialog>
        </div>
    </div>
</template>
<script>
import { mapState, mapActions } from 'pinia';
import { useCreditScoreStore } from '@/store/creditscore';
import ConfirmationDialog from '@/components/ui/confirmation-dialog/confirmation-dialog.vue';
import Icon from '@/components/ui/icon/icon.vue';
import CreditscoreStart from './start-tracking.vue';
import LoadingState from '@/components/ui/loading-state/loading-state.vue';

export default {
    props: {
        emptyState: {
            type: Boolean,
            default: false,
        },
        paginatedScores: {
            type: Array,
            default: () => [],
        },
    },
    components: {
        ConfirmationDialog,
        Icon,
        CreditscoreStart,
        LoadingState,
    },
    data: () => ({}),
    directives: {},
    computed: {
        ...mapState(useCreditScoreStore, [
            'userScoreData',
            'showDeleteScoreModal',
            'activeScore',
            'loadingUserCreditScore',
        ]),
    },
    watch: {},
    methods: {
        ...mapActions(useCreditScoreStore, [
            'deleteScore',
            'setActiveScore',
            'openCreditScoreModal',
            'openDeleteScoreModal',
            'closeDeleteScoreModal',
        ]),
        closeCreditScoreModal() {},
        handleDeleteClick(score) {
            this.setActiveScore(score);
            this.openDeleteScoreModal();
        },
        confirmDeleteScore() {
            if (this.activeScore) {
                this.deleteScore(this.activeScore);
                this.closeDeleteScoreModal();
            }
        },
        formatDate(dateString) {
            if (!dateString) return '';

            const parts = dateString.split('/');
            if (parts.length !== 3) return dateString;

            const month = parseInt(parts[0], 10);
            const day = parseInt(parts[1], 10);
            const year = parseInt(parts[2], 10);

            const monthNames = [
                'Jan',
                'Feb',
                'Mar',
                'Apr',
                'May',
                'Jun',
                'Jul',
                'Aug',
                'Sep',
                'Oct',
                'Nov',
                'Dec',
            ];

            return `${monthNames[month - 1]} ${day}, ${year}`;
        },
    },
};
</script>
